/**
 * The variable set every invoice template renders (decisions-log T2, T8). Issued invoices freeze
 * exactly this object in `invoices.snapshot_json`; re-rendering an issued invoice always uses it.
 */
import { formatTemplateDate } from './dates';
import { minorToNumber, minutesToHours, rateToNumber, sumMinor } from './money';
import { FIXED_FEE_LABEL, type LinePricing } from './schemas/compensation';

export interface TemplateLineVars {
  readonly description: string;
  readonly period_start: string;
  readonly period_end: string;
  /** Printed as String(Number): 26.625, 36 */
  readonly rate: number;
  /** Printed as String(Number): 117.5, 168 */
  readonly hours: number;
  /** Printed as String(Number): 3128.43, 6048 */
  readonly amount: number;
  /** Fixed-fee and stage lines only (P6): the Rate cell prints `rate_label`, the Hours cell stays empty. */
  readonly fixed?: true;
  readonly rate_label?: string;
}

export interface InvoiceTemplateVars {
  readonly invoice: {
    readonly number: string;
    readonly date: string;
    readonly period_start: string;
    readonly period_end: string;
    readonly total: number;
    readonly currency: string;
    readonly payment_terms: string;
    readonly contract: string;
    /** Issued in another currency than the price (V185): which rate converted it and when it is paid. */
    readonly fx_note?: string;
  };
  readonly lines: readonly TemplateLineVars[];
  readonly client: {
    readonly bill_to: string;
    readonly contact_name: string;
    readonly contact_email: string;
    readonly place: string;
  };
  readonly issuer: {
    readonly name: string;
    readonly subtitle: string;
    readonly registration_number: string;
    readonly registration_date: string;
    readonly address: string;
    readonly phone: string;
    readonly email: string;
  };
  readonly bank: {
    readonly iban: string;
    readonly transit_account: string;
    readonly name: string;
    readonly swift: string;
    readonly address: string;
    /** The correspondent bank (V140); '' when the account has none. */
    readonly correspondent_name: string;
    readonly correspondent_swift: string;
    readonly correspondent_aba: string;
  };
}

export interface TemplateLineInput {
  readonly description: string;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
  readonly minutes: number;
  readonly rateE4: number;
  readonly amountMinor: number;
  /** Omitted or 'hourly' prints hours × rate; any other pricing prints "Fixed fee" (P6). */
  readonly pricing?: LinePricing;
}

export interface TemplateVarsInput {
  readonly invoice: {
    readonly number: string | null;
    readonly invoiceDate: string;
    readonly periodStart: string | null;
    readonly periodEnd: string | null;
    readonly currency: string;
    readonly paymentTerms: string | null;
    readonly contractTitle: string | null;
    readonly fx?: TemplateFxInput | null;
  };
  readonly lines: readonly TemplateLineInput[];
  readonly client: {
    readonly payerText: string;
    readonly place: string | null;
    readonly contactName: string | null;
    readonly contactEmail: string | null;
  };
  readonly issuer: {
    readonly name: string;
    readonly invoiceSubtitle: string | null;
    readonly registrationNumber: string | null;
    readonly registrationDate: string | null;
    readonly address: string | null;
    readonly phone: string | null;
    readonly email: string | null;
  };
  readonly bank: {
    readonly iban: string | null;
    readonly transitAccount: string | null;
    readonly bankName: string | null;
    readonly swift: string | null;
    readonly bankAddress: string | null;
    readonly intermediaryBankName: string | null;
    readonly intermediarySwift: string | null;
    readonly intermediaryAba: string | null;
  } | null;
}

const text = (value: string | null | undefined): string => value ?? '';

/** Issuer registration dates are stored ISO but printed like every other template date. */
function printableDate(value: string | null): string {
  if (!value) return '';
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatTemplateDate(value) : value;
}

/** Hourly lines carry no extra keys, so their frozen snapshots stay as they were before P6. */
const fixedVars = (line: TemplateLineInput) =>
  line.pricing === undefined || line.pricing === 'hourly' ? {} : { fixed: true as const, rate_label: FIXED_FEE_LABEL };

/** The conversion of an invoice issued in another currency than the price (V185). */
export interface TemplateFxInput {
  readonly priceCurrency: string;
  readonly priceTotalMinor: number;
  readonly rule: 'invoice_date' | 'payment_date' | 'day_before_payment' | 'period_end';
  readonly rateE6: number;
  readonly rateDate: string;
}

const RATE_DECIMALS = 4;
const RATE_SCALE = 1_000_000;
const AMOUNT_FORMAT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** «… of the payment date» / «… of the day before the payment date» for the payment rules. */
const PAYMENT_DAY: Partial<Record<TemplateFxInput['rule'], string>> = {
  payment_date: 'the payment date',
  day_before_payment: 'the day before the payment date',
};

/** The PDF's English note on the rate (invoice PDFs stay English, V45). */
export function fxNoteOf(fx: TemplateFxInput, currency: string): string {
  const rate = `1 ${fx.priceCurrency} = ${(fx.rateE6 / RATE_SCALE).toFixed(RATE_DECIMALS)} ${currency}`;
  const day = formatTemplateDate(fx.rateDate);
  const payment = PAYMENT_DAY[fx.rule];
  if (!payment) return `Amounts converted from ${fx.priceCurrency} at the official exchange rate of ${day} (${rate}).`;
  const equivalent = `${fx.priceCurrency} ${AMOUNT_FORMAT.format(minorToNumber(fx.priceTotalMinor))}`;
  return `Payable in ${currency} at the official exchange rate of ${payment}: the equivalent of ${equivalent}. Amounts shown at the rate of ${day} (${rate}).`;
}

export function buildTemplateVars(input: TemplateVarsInput): InvoiceTemplateVars {
  const lines = input.lines.map((line) => ({
    description: line.description,
    period_start: formatTemplateDate(line.periodStart ?? input.invoice.periodStart),
    period_end: formatTemplateDate(line.periodEnd ?? input.invoice.periodEnd),
    rate: rateToNumber(line.rateE4),
    hours: minutesToHours(line.minutes),
    amount: minorToNumber(line.amountMinor),
    ...fixedVars(line),
  }));
  const bank = input.bank;
  return {
    invoice: {
      number: text(input.invoice.number),
      date: formatTemplateDate(input.invoice.invoiceDate),
      period_start: formatTemplateDate(input.invoice.periodStart),
      period_end: formatTemplateDate(input.invoice.periodEnd),
      total: minorToNumber(sumMinor(input.lines.map((l) => l.amountMinor))),
      currency: input.invoice.currency,
      payment_terms: text(input.invoice.paymentTerms),
      contract: text(input.invoice.contractTitle),
      ...(input.invoice.fx ? { fx_note: fxNoteOf(input.invoice.fx, input.invoice.currency) } : {}),
    },
    lines,
    client: {
      bill_to: input.client.payerText,
      contact_name: text(input.client.contactName),
      contact_email: text(input.client.contactEmail),
      place: text(input.client.place),
    },
    issuer: {
      name: input.issuer.name,
      subtitle: text(input.issuer.invoiceSubtitle),
      registration_number: text(input.issuer.registrationNumber),
      registration_date: printableDate(input.issuer.registrationDate),
      address: text(input.issuer.address),
      phone: text(input.issuer.phone),
      email: text(input.issuer.email),
    },
    bank: {
      iban: text(bank?.iban),
      transit_account: text(bank?.transitAccount),
      name: text(bank?.bankName),
      swift: text(bank?.swift),
      address: text(bank?.bankAddress),
      correspondent_name: text(bank?.intermediaryBankName),
      correspondent_swift: text(bank?.intermediarySwift),
      correspondent_aba: text(bank?.intermediaryAba),
    },
  };
}
